import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Stepper from '../../components/Stepper';
import { WIZARD_STEPS, WizardStatus } from '../../components/WizardShell';
import { api, type DnaResponse } from '../../lib/api';
import { cx } from '../../lib/cx';

/** Plain-English names for the axes. The customer never sees a tag id. */
const AXIS_ORDER: { key: string; label: string; lead: string }[] = [
  { key: 'era', label: 'Overall style', lead: 'You lean' },
  { key: 'massing', label: 'Shape', lead: 'The buildings you chose are' },
  { key: 'roof', label: 'Roof', lead: 'Topped with' },
  { key: 'primaryMaterial', label: 'Main material', lead: 'Mostly' },
  { key: 'glazing', label: 'Windows and glass', lead: 'Glass handled as' },
  { key: 'entry', label: 'Entrance', lead: 'The way in reads as' },
  { key: 'palette', label: 'Color', lead: 'In a palette of' },
  { key: 'signature', label: 'Signature feature', lead: 'The thing people remember' },
];

const NUMERIC_BANDS: Record<string, { label: string; low: string; high: string; words: string[] }> = {
  ornament: {
    label: 'Level of detail',
    low: 'Plain',
    high: 'Highly detailed',
    words: ['completely plain', 'very restrained', 'moderately detailed', 'richly detailed', 'highly ornamented'],
  },
  verticality: {
    label: 'Proportion',
    low: 'Long and low',
    high: 'Tall and vertical',
    words: ['long and low', 'mostly horizontal', 'balanced', 'tall-feeling', 'strongly vertical'],
  },
  symmetry: {
    label: 'Symmetry',
    low: 'Asymmetric',
    high: 'Symmetrical',
    words: ['deliberately asymmetric', 'loosely balanced', 'balanced', 'largely symmetrical', 'strictly symmetrical'],
  },
};

function Meter({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, (value / 4) * 100));
  return (
    <span className="mt-2 block h-[6px] w-full rounded-full bg-grey-200">
      <span className="block h-full rounded-full bg-green-600" style={{ width: `${pct}%` }} />
    </span>
  );
}

export default function Results() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DnaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api
      .getDna(id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError('We could not read your picks. Try again in a moment.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const resolve = async (axis: string, value: string) => {
    if (!id || !data) return;
    setResolving(axis);
    try {
      const next = await api.computeDna(id, { ...data.resolutions, [axis]: value });
      setData(next);
    } catch {
      setError('That did not save. Try again in a moment.');
    } finally {
      setResolving(null);
    }
  };

  if (error && !data) return <WizardStatus message={error} />;
  if (!data) return <WizardStatus message="Reading your picks…" />;

  if (!data.dna) {
    return (
      <AppShell>
        <h1 className="t-h1 text-ink">Pick some photographs first</h1>
        <p className="t-body measure mt-4 text-grey-600">
          We read your taste from the buildings you choose, so there is nothing to read yet.
        </p>
        <div className="mt-8">
          <Button onClick={() => navigate(`/project/${id}/styles`)}>Go to the photos</Button>
        </div>
      </AppShell>
    );
  }

  const { dna, brief, picks } = data;

  return (
    <AppShell>
      <Stepper steps={WIZARD_STEPS} current={5} />

      <div className="mt-10">
        <p className="t-eyebrow text-green-600">Your style</p>
        <h1 className="t-h1 mt-3 text-ink">What your picks say</h1>
        <p className="t-body-lg measure mt-4 text-grey-600">
          This is what we read from the {picks.length} buildings you chose. Nothing here is
          fixed — it is the starting direction, and you can change it.
        </p>
      </div>

      {/* 1. Their picks, small, so the reading below is anchored to something real. */}
      <section className="mt-10">
        <h2 className="t-h3 text-ink">The ones you chose</h2>
        <ul className="mt-3 flex flex-wrap gap-2">
          {picks.map((p, i) => (
            <li key={p.id} className="relative">
              <img
                src={p.url}
                alt={p.caption ?? ''}
                className="h-24 w-32 rounded-sm border border-grey-200 object-cover"
                loading="lazy"
              />
              <span className="t-numeral absolute top-1 left-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs text-white">
                {i + 1}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* 2. The reading itself, in the vocabulary labels, never the tag ids. */}
      <section className="mt-14">
        <h2 className="t-h2 text-ink">What we read</h2>
        <ul className="mt-6 grid gap-4 sm:grid-cols-2">
          {AXIS_ORDER.map((axis) => {
            const ranked = dna.categorical[axis.key];
            if (!ranked?.length) return null;
            const [first, second] = ranked;
            return (
              <li
                key={axis.key}
                className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm"
              >
                <p className="t-meta text-grey-600">{axis.label}</p>
                <p className="t-h3 mt-1 text-ink">{first.label}</p>
                {second && second.weight > 0.15 && (
                  <p className="t-meta mt-2 text-grey-600">
                    with some {second.label.toLowerCase()}
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        <ul className="mt-4 grid gap-4 sm:grid-cols-3">
          {Object.entries(NUMERIC_BANDS).map(([key, band]) => {
            const n = dna.numeric[key];
            if (!n) return null;
            return (
              <li key={key} className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                <p className="t-meta text-grey-600">{band.label}</p>
                <p className="t-h3 mt-1 text-ink">
                  {band.words[Math.round(n.mean)] ?? band.words[2]}
                </p>
                <Meter value={n.mean} />
                <p className="t-meta mt-2 flex justify-between text-grey-600">
                  <span>{band.low}</span>
                  <span>{band.high}</span>
                </p>
              </li>
            );
          })}
        </ul>
      </section>

      {/* 3. Conflicts, framed as something useful we noticed. */}
      {dna.conflicts.length > 0 && (
        <section className="mt-14">
          <h2 className="t-h2 text-ink">Two directions to choose between</h2>
          <p className="t-body measure mt-3 text-grey-600">
            Your picks are split. That is genuinely useful to know now rather than after a
            committee meeting — it usually means two people want two different buildings.
          </p>

          <div className="mt-6 flex flex-col gap-4">
            {dna.conflicts.map((c) => (
              <div
                key={c.axis}
                className="rounded-lg border border-grey-200 bg-grey-050 p-6 shadow-sm"
              >
                <p className="t-h3 text-ink">{c.axisLabel}</p>
                <p className="t-body measure mt-2 text-grey-600">{c.message}</p>
                {c.options.length === 2 && (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {c.options.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        disabled={resolving !== null}
                        onClick={() => void resolve(c.axis, o.value)}
                        className={cx(
                          'min-h-tap rounded-full border border-green-600 bg-white px-6 text-[0.95rem] font-semibold text-green-700 transition-colors',
                          'hover:bg-green-050 disabled:cursor-wait disabled:opacity-50',
                        )}
                      >
                        {resolving === c.axis ? 'Updating…' : `${o.label} should lead`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {Object.keys(data.resolutions).length > 0 && (
        <div className="mt-8">
          <Callout tone="confirm" title="Noted">
            We have leaned your profile the way you asked. You can still change any of the
            answers behind it.
          </Callout>
        </div>
      )}

      {/* 4. The written brief — the thing a board can hand to an architect. */}
      <section className="mt-14">
        <h2 className="t-h2 text-ink">Your design direction</h2>
        <p className="t-meta measure mt-2 text-grey-600">
          Written out, so you can take it to a design professional without needing the
          pictures.
        </p>
        {data.briefIsPartial && (
          <p className="t-meta measure mt-3 text-grey-600">
            Your picks pull in more than one direction, so this describes the strongest
            single reading of them —{' '}
            <span className="t-numeral">{data.briefCoveredPicks}</span> of your{' '}
            <span className="t-numeral">{picks.length}</span>. Settling the questions above
            will fold the rest in.
          </p>
        )}
        <div className="mt-5 rounded-lg border-l-[3px] border-l-green-600 border-grey-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="t-body-lg measure text-ink">{brief}</p>
        </div>
      </section>

      <div className="mt-12 flex flex-col-reverse gap-3 border-t border-grey-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="secondary" block onClick={() => navigate(`/project/${id}/styles`)}>
          Change my picks
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button variant="secondary" block onClick={() => navigate(`/project/${id}/plan`)}>
            Site, budget and power
          </Button>
          <Button block onClick={() => navigate(`/project/${id}/concepts`)}>
            Generate my concepts
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
